import React, { useState, useEffect, useLayoutEffect, useCallback, useRef, useMemo } from "react";
import config from "../config";
import "./OpsDashboard.css";

const POLL_INTERVAL = 20 * 60 * 1000; // 20 minutes

// ── Status labels ──
const STATUS_LABELS = {
  pct_other: "Internal Review",
  pct_in_queue: "In Queue",
  pct_ready: "Ready",
  pct_in_process: "In Process",
  pct_finished: "Finished",
  pct_closed: "Closed",
};

const STATUS_ORDER = [
  "pct_in_queue",
  "pct_ready",
  "pct_in_process",
  "pct_other",
  "pct_finished",
  "pct_closed",
];

// ── Ideal percentages by cycle day (1-14) ──
const IDEAL_BY_DAY = {
  1:  { pct_other: 10, pct_in_queue: 40, pct_ready: 25, pct_in_process: 20, pct_finished:  5, pct_closed:  0 },
  2:  { pct_other:  8, pct_in_queue: 35, pct_ready: 25, pct_in_process: 25, pct_finished:  7, pct_closed:  0 },
  3:  { pct_other:  6, pct_in_queue: 28, pct_ready: 22, pct_in_process: 30, pct_finished: 12, pct_closed:  2 },
  4:  { pct_other:  5, pct_in_queue: 22, pct_ready: 18, pct_in_process: 32, pct_finished: 18, pct_closed:  5 },
  5:  { pct_other:  4, pct_in_queue: 16, pct_ready: 15, pct_in_process: 30, pct_finished: 25, pct_closed: 10 },
  6:  { pct_other:  3, pct_in_queue: 12, pct_ready: 12, pct_in_process: 28, pct_finished: 28, pct_closed: 17 },
  7:  { pct_other:  2, pct_in_queue:  8, pct_ready: 10, pct_in_process: 25, pct_finished: 30, pct_closed: 25 },
  8:  { pct_other:  2, pct_in_queue:  5, pct_ready:  8, pct_in_process: 20, pct_finished: 30, pct_closed: 35 },
  9:  { pct_other:  1, pct_in_queue:  3, pct_ready:  5, pct_in_process: 16, pct_finished: 28, pct_closed: 47 },
  10: { pct_other:  1, pct_in_queue:  2, pct_ready:  3, pct_in_process: 12, pct_finished: 22, pct_closed: 60 },
  11: { pct_other:  0, pct_in_queue:  1, pct_ready:  2, pct_in_process:  8, pct_finished: 17, pct_closed: 72 },
  12: { pct_other:  0, pct_in_queue:  0, pct_ready:  1, pct_in_process:  5, pct_finished: 12, pct_closed: 82 },
  13: { pct_other:  0, pct_in_queue:  0, pct_ready:  0, pct_in_process:  3, pct_finished:  7, pct_closed: 90 },
  14: { pct_other:  0, pct_in_queue:  0, pct_ready:  0, pct_in_process:  0, pct_finished:  2, pct_closed: 98 },
};

const HIGHER_IS_BETTER = new Set(["pct_in_process", "pct_finished", "pct_closed"]);
const LOWER_IS_BETTER = new Set(["pct_other", "pct_in_queue", "pct_ready"]);

const getCycleDay = (wsNum, currentCycles) => {
  if (!currentCycles || currentCycles.length === 0) return null;
  const cycle = currentCycles.find(
    (c) => String(c.workstream_num) === String(wsNum)
  );
  if (!cycle || !cycle.cycle_start || !cycle.cycle_end) return null;
  const start = new Date(cycle.cycle_start);
  const now = new Date();
  const diffMs = now - start;
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  return Math.max(1, Math.min(14, diffDays + 1));
};

const interpolateColor = (score) => {
  const s = Math.max(0, Math.min(1, score));
  if (s <= 0.5) {
    const t = s / 0.5;
    return `rgb(220, ${Math.round(50 + t * 150)}, 30)`;
  }
  const t = (s - 0.5) / 0.5;
  return `rgb(${Math.round(220 - t * 185)}, ${Math.round(200 - t * 30)}, ${Math.round(30 + t * 70)})`;
};

const getHealthColor = (key, pct, cycleDay) => {
  if (pct === 0) return undefined;
  const day = cycleDay || 7;
  const ideal = IDEAL_BY_DAY[day]?.[key];
  if (ideal === undefined) return "var(--text-primary, #223651)";

  if (HIGHER_IS_BETTER.has(key)) {
    if (ideal === 0) return interpolateColor(1);
    const score = Math.min(pct / ideal, 1);
    return interpolateColor(score);
  }

  if (LOWER_IS_BETTER.has(key)) {
    if (ideal === 0) {
      return pct > 0 ? interpolateColor(0) : interpolateColor(1);
    }
    const score = Math.min(1, 1 - (pct - ideal) / ideal);
    return interpolateColor(Math.max(0, score));
  }

  return "var(--text-primary, #223651)";
};

// ── Hours display helper ──
const formatHours = (hours) => {
  if (!hours) return null;
  const h = parseFloat(hours);
  if (h === 8) return null;
  if (h === 4) return <span className="ops-person-detail">½</span>;
  return <span className="ops-person-detail">{h}h</span>;
};

// ── People Card with optional vertical carousel ──
// eslint-disable-next-line no-unused-vars
const SCROLL_SPEED = 0.3;

// eslint-disable-next-line no-unused-vars
const PeopleCard = ({ title, icon, items, nameKey, color, emptyMsg }) => {
  const listRef = useRef(null);
  const innerRef = useRef(null);
  const offsetRef = useRef(0);
  const animRef = useRef(null);
  const [overflows, setOverflows] = useState(false);

  const displayItems = useMemo(() => {
    if (!overflows) return items;
    return [...items, ...items];
  }, [items, overflows]);

  useEffect(() => {
    const checkOverflow = () => {
      if (listRef.current && innerRef.current) {
        const containerH = listRef.current.clientHeight;
        const contentH = innerRef.current.scrollHeight / (overflows ? 2 : 1);
        setOverflows(contentH > containerH);
      }
    };
    checkOverflow();
    window.addEventListener("resize", checkOverflow);
    return () => window.removeEventListener("resize", checkOverflow);
  }, [items, overflows]);

  useEffect(() => {
    if (!overflows || !innerRef.current) return;
    const singleSetHeight = innerRef.current.scrollHeight / 2;
    if (singleSetHeight <= 0) return;
    offsetRef.current = 0;

    const animate = () => {
      offsetRef.current += SCROLL_SPEED;
      if (offsetRef.current >= singleSetHeight) {
        offsetRef.current -= singleSetHeight;
      }
      if (innerRef.current) {
        innerRef.current.style.transform = `translateY(${-offsetRef.current}px)`;
      }
      animRef.current = requestAnimationFrame(animate);
    };

    animRef.current = requestAnimationFrame(animate);
    return () => {
      if (animRef.current) cancelAnimationFrame(animRef.current);
    };
  }, [overflows]);

  return (
    <div className="ops-card" style={{ borderTop: `3px solid ${color}` }}>
      <div className="ops-card-header">
        <span className="ops-card-icon">{icon}</span>
        <h3 className="ops-card-title">{title}</h3>
        <span className="ops-card-count" style={{ background: color }}>
          {items.length}
        </span>
      </div>
      {items.length === 0 ? (
        <div className="ops-card-empty">{emptyMsg}</div>
      ) : (
        <div
          className={`ops-people-list-container ${overflows ? "ops-people-list-masked" : ""}`}
          ref={listRef}
        >
          <ul className="ops-people-list" ref={innerRef}>
            {displayItems.map((item, i) => (
              <li key={i} className="ops-person">
                <span className="ops-person-name">{item[nameKey]}</span>
                {formatHours(item.hours)}
                {item.title && (
                  <span className="ops-person-detail">{item.title}</span>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
};

// ── Birthdays & Anniversaries Week View ──
// eslint-disable-next-line no-unused-vars
const DAY_NAMES = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

// eslint-disable-next-line no-unused-vars
const BirthdaysAnniversaries = ({ data }) => {
  // useMemo so `items` keeps a stable identity when data?.items hasn't
  // changed — otherwise the `byDay` useMemo below would invalidate every render.
  const items = useMemo(() => data?.items || [], [data?.items]);
  const todayIndex = data?.today_index ?? new Date().getDay();
  const weekDates = data?.week_dates || [];

  // Group items by day_index
  const byDay = useMemo(() => {
    const grouped = Array.from({ length: 7 }, () => []);
    items.forEach((item) => {
      const idx = item.day_index;
      if (idx >= 0 && idx < 7) {
        grouped[idx].push(item);
      }
    });
    return grouped;
  }, [items]);

  // Format month-day for column header
  const formatDate = (mmdd) => {
    if (!mmdd) return "";
    const [m, d] = mmdd.split("-");
    return `${parseInt(m)}/${parseInt(d)}`;
  };

  return (
    <div
      className="ops-card ops-bday-card"
      style={{ borderTop: "3px solid #ec4899" }}
    >
      <div className="ops-card-header">
        <span className="ops-card-icon">🎂</span>
        <h3 className="ops-card-title">Birthdays & Anniversaries</h3>
        <span className="ops-card-count" style={{ background: "#ec4899" }}>
          {items.length}
        </span>
      </div>
      <div className="ops-bday-week">
        {DAY_NAMES.map((dayName, idx) => (
          <div
            key={idx}
            className={`ops-bday-day ${idx === todayIndex ? "ops-bday-day-today" : ""}`}
          >
            <div className="ops-bday-day-header">
              <span className="ops-bday-day-name">{dayName}</span>
              {weekDates[idx] && (
                <span className="ops-bday-day-date">
                  {formatDate(weekDates[idx])}
                </span>
              )}
            </div>
            <div className="ops-bday-day-items">
              {byDay[idx].length === 0 ? (
                <div className="ops-bday-empty">—</div>
              ) : (
                byDay[idx].map((item, i) => (
                  <div key={i} className="ops-bday-item">
                    <span className="ops-bday-icon">
                      {item.event_type === "birthday" ? "🎂" : "🎉"}
                    </span>
                    <div className="ops-bday-info">
                      <span className="ops-bday-name">{item.full_name}</span>
                      {item.event_type === "anniversary" &&
                        item.years_at_company && (
                          <span className="ops-bday-years">
                            {item.years_at_company} yr{item.years_at_company !== 1 ? "s" : ""}
                          </span>
                        )}
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// ── Workstream Revenue Table ──
const WorkstreamTable = ({ data, currentCycles }) => (
  <div
    className="ops-card ops-workstream-card"
    style={{ borderTop: "3px solid #3b82f6" }}
  >
    <div className="ops-card-header">
      <span className="ops-card-icon">📊</span>
      <h3 className="ops-card-title">Workstream Hours by Status</h3>
    </div>
    {!data || data.length === 0 ? (
      <div className="ops-card-empty">No workstream data available</div>
    ) : (
      <div className="ops-ws-table-wrapper">
        <table className="ops-ws-table">
          <thead>
            <tr>
              <th className="ops-ws-th ops-ws-th-ws">Workstream</th>
              {STATUS_ORDER.map((key) => (
                <th key={key} className="ops-ws-th">
                  {STATUS_LABELS[key]}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.map((ws, i) => {
              const day = getCycleDay(ws.workstream_num, currentCycles);
              return (
                <tr key={i} className="ops-ws-tr">
                  <td className="ops-ws-td ops-ws-td-ws">
                    WS {ws.workstream_num}
                    {day && <span className="ops-ws-day">Day {day}</span>}
                  </td>
                  {STATUS_ORDER.map((key) => {
                    const pct = parseFloat(ws[key]) || 0;
                    return (
                      <td key={key} className="ops-ws-td">
                        <span
                          className="ops-ws-pct"
                          style={{ color: getHealthColor(key, pct, day) }}
                        >
                          {pct > 0 ? `${pct}%` : "—"}
                        </span>
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    )}
  </div>
);

// ── Role Revenue by Status (split by workstream + swimlane) ──
// Time-based speed so it stays smooth at any frame rate. 14 px/s ≈ the old
// 0.2 px/frame, but advancing by real elapsed time kills frame-to-frame jitter.
const ROLE_SCROLL_PX_PER_SEC = 14;
// Extra pixels between the column-header row and the pinned WS label.
// Used by the manual-sticky JS in RoleRevenueTable's animation loop.
const ROLE_STICKY_EXTRA = 4;

// Format an hour count for display: whole numbers as-is, otherwise one decimal.
const formatHourCount = (h) => {
  const r = Math.round(h * 10) / 10;
  return Number.isInteger(r) ? String(r) : r.toFixed(1);
};

const RoleRevenueTable = ({
  data,
  currentCycles,
  nameLabel = "Role",
  fitNameColumn = false,
  showHours = false,
}) => {
  const wrapperRef = useRef(null);
  const tableRef = useRef(null);
  const tbodyRef = useRef(null);
  const scrollRef = useRef(0);
  const animRef = useRef(null);
  const [overflows, setOverflows] = useState(false);
  // When fitNameColumn is on, each status column is locked to a width that
  // fits its longest single header word (so "INTERNAL"/"PROCESS"/etc. never
  // wrap mid-character). The name column is left auto so it absorbs whatever
  // room is left, with ellipsis on overflow.
  const [statusColWidths, setStatusColWidths] = useState(null);
  // Live thead height — drives both the sticky-WS `top` (via CSS var) and the
  // scroll-engage baseOffset, so wrapping headers don't collide with WS cells.
  const [theadH, setTheadH] = useState(28);

  // Drop rows whose every pct_* is null/0 — they have no meaningful data.
  const rows = useMemo(
    () =>
      (data || []).filter((r) =>
        STATUS_ORDER.some((k) => {
          const v = parseFloat(r[k]);
          return !isNaN(v) && v > 0;
        })
      ),
    [data]
  );
  const byWs = useMemo(() => {
    const m = {};
    for (const r of rows) {
      const k = String(r.workstream_num);
      if (!m[k]) m[k] = [];
      m[k].push(r);
    }
    return m;
  }, [rows]);
  const wsKeys = useMemo(
    () => Object.keys(byWs).sort((a, b) => Number(a) - Number(b)),
    [byWs]
  );

  // Measure content vs container to decide whether to animate.
  useEffect(() => {
    const check = () => {
      if (!wrapperRef.current || !tableRef.current) return;
      const wrapH = wrapperRef.current.clientHeight;
      const contentH = tableRef.current.scrollHeight / (overflows ? 2 : 1);
      setOverflows(contentH > wrapH + 2);
    };
    check();
    window.addEventListener("resize", check);
    return () => window.removeEventListener("resize", check);
  }, [rows, overflows]);

  // Observe the thead height (it changes when column headers wrap to 2 lines).
  useLayoutEffect(() => {
    if (!tableRef.current) return;
    const thead = tableRef.current.querySelector("thead");
    if (!thead) return;
    const measure = () => {
      const h = thead.getBoundingClientRect().height;
      setTheadH(Math.max(1, Math.ceil(h)));
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(thead);
    return () => ro.disconnect();
  }, [rows.length]);

  // When fitNameColumn is on, lock each status column to a width that fits
  // its longest single header word. Canvas measureText doesn't honor
  // text-transform or letter-spacing, so we uppercase the label first and
  // pad for the 0.04em letter spacing. Name column gets the remainder via
  // colgroup auto + table-layout: fixed.
  useLayoutEffect(() => {
    if (!fitNameColumn || !tableRef.current) {
      setStatusColWidths(null);
      return;
    }
    const sampleHeader = tableRef.current.querySelector(
      ".ops-role-th:not(.ops-role-th-ws):not(.ops-role-th-name)"
    );
    if (!sampleHeader) return;
    const style = window.getComputedStyle(sampleHeader);
    const fontSizePx = parseFloat(style.fontSize) || 10;
    const font = `${style.fontWeight} ${style.fontSize} ${style.fontFamily}`;
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");
    ctx.font = font;
    const padL = parseFloat(style.paddingLeft) || 0;
    const padR = parseFloat(style.paddingRight) || 0;
    // letter-spacing is 0.04em — measureText ignores it, so account manually.
    const letterSpacing = 0.04 * fontSizePx;
    const widths = {};
    for (const key of STATUS_ORDER) {
      const label = (STATUS_LABELS[key] || "").toUpperCase();
      const words = label.split(/\s+/).filter(Boolean);
      let maxWord = 0;
      for (const w of words) {
        const ww = ctx.measureText(w).width + letterSpacing * Math.max(0, w.length - 1);
        if (ww > maxWord) maxWord = ww;
      }
      // +4px buffer absorbs subpixel rounding and the trailing letter-spacing
      // gap that some browsers paint after the last glyph.
      widths[key] = Math.ceil(maxWord + padL + padR + 4);
    }
    setStatusColWidths(widths);
  }, [fitNameColumn]);

  // Auto-scroll the rows via a single transform on <tbody>. The wrapper is
  // overflow:hidden and scrollTop is never touched — that's the key change
  // for smoothness. scrollTop and transform commit on different pipelines,
  // so mixing them produces a sub-pixel hitch every integer-pixel boundary.
  // A pure transform animation is interpolated by the GPU compositor at the
  // device's actual refresh rate, so motion stays smooth on any display.
  useEffect(() => {
    if (!overflows || !wrapperRef.current || !tableRef.current) return;

    // Capture the tbody node once. Using `tbodyRef.current` directly inside
    // the cleanup is what triggers the exhaustive-deps lint — by the time
    // cleanup runs the ref may already point elsewhere.
    const tbody = tbodyRef.current;
    if (!tbody) return;

    // singleH = height of one duplicated copy. We render rows twice and wrap
    // at singleH so the cycle is visually seamless.
    const singleH = tbody.scrollHeight / 2;
    if (singleH <= 0) return;

    const stickyTop = theadH + ROLE_STICKY_EXTRA;

    // Cache per-WS-cell measurements so the per-frame inner loop is pure
    // arithmetic — no layout reads. tdTop/tdH/etc. are layout-positions and
    // are unaffected by the visual transform we apply each frame.
    const wsCellInfo = [];
    const cells = tbody.querySelectorAll(".ops-role-td-ws");
    for (const td of cells) {
      const span = td.querySelector(".ops-role-td-ws-inner");
      if (!span) continue;
      const cs = window.getComputedStyle(td);
      const padTop = parseFloat(cs.paddingTop) || 0;
      wsCellInfo.push({
        span,
        tdTop: td.offsetTop,
        tdH: td.offsetHeight,
        padTop,
        spanH: span.offsetHeight,
      });
    }

    scrollRef.current = 0;

    let lastTs = 0;
    const animate = (ts) => {
      // Time-delta based: speed in CSS px/sec is independent of refresh rate,
      // so 60Hz / 120Hz / 144Hz panels all move at the same wall-clock pace.
      const dt = lastTs ? ts - lastTs : 16.67;
      lastTs = ts;
      scrollRef.current += (ROLE_SCROLL_PX_PER_SEC * dt) / 1000;
      if (scrollRef.current >= singleH) scrollRef.current -= singleH;

      const offset = scrollRef.current;

      tbody.style.transform = `translate3d(0, ${-offset}px, 0)`;

      // Manual sticky for each WS label — replicates
      // `position: sticky; top: stickyTop` since CSS sticky won't engage
      // (wrapper is overflow:hidden with scrollTop locked at 0). We compute
      // the span's would-be visual top after the tbody transform; if it's
      // above stickyTop, push it back down so it visually pins. The shift
      // is capped to keep the span within its td, which gives the correct
      // "the next group pushes the current label off-screen" behavior.
      for (let i = 0; i < wsCellInfo.length; i++) {
        const info = wsCellInfo[i];
        const naturalVisualTop = info.tdTop + info.padTop - offset;
        let shift = 0;
        if (naturalVisualTop < stickyTop) {
          const desired = stickyTop - naturalVisualTop;
          const maxShift = info.tdH - info.padTop - info.spanH;
          shift = Math.min(desired, Math.max(0, maxShift));
        }
        info.span.style.transform = shift
          ? `translate3d(0, ${shift}px, 0)`
          : "";
      }

      animRef.current = requestAnimationFrame(animate);
    };
    animRef.current = requestAnimationFrame(animate);
    return () => {
      if (animRef.current) cancelAnimationFrame(animRef.current);
      tbody.style.transform = "";
      for (const info of wsCellInfo) {
        info.span.style.transform = "";
      }
    };
  }, [overflows, theadH, statusColWidths]);

  const renderGroups = (prefix) =>
    wsKeys.map((wsNum) => {
      const day = getCycleDay(wsNum, currentCycles);
      const group = byWs[wsNum];
      return group.map((row, idx) => (
        <tr key={`${prefix}-${wsNum}-${idx}`} className="ops-role-tr">
          {idx === 0 && (
            <td
              className="ops-role-td ops-role-td-ws"
              rowSpan={group.length}
            >
              <span className="ops-role-td-ws-inner">
                WS {wsNum}
                {day && <span className="ops-ws-day">Day {day}</span>}
              </span>
            </td>
          )}
          <td className="ops-role-td ops-role-td-name">{row.name}</td>
          {STATUS_ORDER.map((key) => {
            // Coloring is always driven by the percentage (vs. the cycle-day
            // ideal); only the displayed value differs between % and hours.
            const pct = parseFloat(row[key]) || 0;
            let display;
            if (showHours) {
              const hrs = parseFloat(row[key.replace("pct_", "hours_")]) || 0;
              display = hrs > 0 ? formatHourCount(hrs) : "—";
            } else {
              display = pct > 0 ? `${pct}%` : "—";
            }
            return (
              <td key={key} className="ops-role-td">
                <span
                  className="ops-role-pct"
                  style={{ color: getHealthColor(key, pct, day) }}
                >
                  {display}
                </span>
              </td>
            );
          })}
        </tr>
      ));
    });

  return (
    <div className="ops-role-table-wrapper" ref={wrapperRef}>
      {rows.length === 0 ? (
        <div className="ops-card-empty">No role data available</div>
      ) : (
        <table className="ops-role-table" ref={tableRef}>
          {fitNameColumn && (
            <colgroup>
              <col style={{ width: "58px" }} />
              {/* Name col: no explicit width → table-layout: fixed gives it
                  whatever's left after WS + status columns, and the cell's
                  nowrap+ellipsis truncates names that don't fit. */}
              <col />
              {STATUS_ORDER.map((key) => (
                <col
                  key={key}
                  style={
                    statusColWidths?.[key]
                      ? { width: `${statusColWidths[key]}px` }
                      : undefined
                  }
                />
              ))}
            </colgroup>
          )}
          <thead>
            <tr>
              <th className="ops-role-th ops-role-th-ws">WS</th>
              <th className="ops-role-th ops-role-th-name">{nameLabel}</th>
              {STATUS_ORDER.map((key) => (
                <th key={key} className="ops-role-th">
                  {STATUS_LABELS[key]}
                </th>
              ))}
            </tr>
          </thead>
          <tbody ref={tbodyRef}>
            {renderGroups("a")}
            {overflows && renderGroups("b")}
          </tbody>
        </table>
      )}
    </div>
  );
};

// ── Static (non-scrolling) version that fits all rows via CSS grid ──
// eslint-disable-next-line no-unused-vars
const RoleRevenueStatic = ({ data, currentCycles, nameLabel = "Role" }) => {
  // useMemo so `rows` keeps a stable identity when `data` hasn't changed,
  // preventing the dependent `byWs` useMemo from invalidating every render.
  const rows = useMemo(() => data || [], [data]);
  const byWs = useMemo(() => {
    const m = {};
    for (const r of rows) {
      const k = String(r.workstream_num);
      if (!m[k]) m[k] = [];
      m[k].push(r);
    }
    return m;
  }, [rows]);
  const wsKeys = useMemo(
    () => Object.keys(byWs).sort((a, b) => Number(a) - Number(b)),
    [byWs]
  );

  const flatRows = [];
  wsKeys.forEach((wsNum) => {
    byWs[wsNum].forEach((row) => flatRows.push({ wsNum, row }));
  });

  if (rows.length === 0) {
    return <div className="ops-card-empty">No data available</div>;
  }

  const gridStyle = {
    gridTemplateRows: `auto repeat(${flatRows.length}, minmax(0, 1fr))`,
  };

  return (
    <div className="ops-role-static" style={gridStyle}>
      {/* Column headers */}
      <div className="ops-role-static-head ops-role-static-head-ws">WS</div>
      <div className="ops-role-static-head ops-role-static-head-name">
        {nameLabel}
      </div>
      {STATUS_ORDER.map((key) => (
        <div key={key} className="ops-role-static-head">
          {STATUS_LABELS[key]}
        </div>
      ))}

      {/* WS labels spanning their group's rows */}
      {wsKeys.map((wsNum) => {
        const group = byWs[wsNum];
        const day = getCycleDay(wsNum, currentCycles);
        const startIdx = flatRows.findIndex((f) => f.wsNum === wsNum);
        return (
          <div
            key={`ws-${wsNum}`}
            className="ops-role-static-ws"
            style={{
              gridColumn: 1,
              gridRow: `${2 + startIdx} / span ${group.length}`,
            }}
          >
            <span>WS {wsNum}</span>
            {day && <span className="ops-ws-day">Day {day}</span>}
          </div>
        );
      })}

      {/* Data cells */}
      {flatRows.map((f, i) => {
        const day = getCycleDay(f.wsNum, currentCycles);
        return (
          <React.Fragment key={i}>
            <div
              className="ops-role-static-name"
              style={{ gridRow: 2 + i, gridColumn: 2 }}
              title={f.row.name}
            >
              {f.row.name}
            </div>
            {STATUS_ORDER.map((key, colIdx) => {
              const pct = parseFloat(f.row[key]) || 0;
              return (
                <div
                  key={key}
                  className="ops-role-static-cell"
                  style={{
                    gridRow: 2 + i,
                    gridColumn: 3 + colIdx,
                    color: getHealthColor(key, pct, day),
                  }}
                >
                  {pct > 0 ? `${pct}%` : "—"}
                </div>
              );
            })}
          </React.Fragment>
        );
      })}
    </div>
  );
};

// ── Client Commitments ──
const COMMIT_SCROLL_SPEED = 0.075;

// Independently scrolling vertical list; only animates when content overflows.
const ScrollingLane = ({ items, renderRow, emptyMsg, rowClass }) => {
  const containerRef = useRef(null);
  const innerRef = useRef(null);
  const offsetRef = useRef(0);
  const animRef = useRef(null);
  const [overflows, setOverflows] = useState(false);

  const displayItems = useMemo(() => {
    if (!overflows) return items;
    return [...items, ...items];
  }, [items, overflows]);

  useEffect(() => {
    const check = () => {
      if (containerRef.current && innerRef.current) {
        const containerH = containerRef.current.clientHeight;
        const contentH = innerRef.current.scrollHeight / (overflows ? 2 : 1);
        setOverflows(contentH > containerH);
      }
    };
    check();
    window.addEventListener("resize", check);
    return () => window.removeEventListener("resize", check);
  }, [items, overflows]);

  useEffect(() => {
    if (!overflows || !innerRef.current) return;
    const singleH = innerRef.current.scrollHeight / 2;
    if (singleH <= 0) return;
    offsetRef.current = 0;

    const animate = () => {
      offsetRef.current += COMMIT_SCROLL_SPEED;
      if (offsetRef.current >= singleH) offsetRef.current -= singleH;
      if (innerRef.current) {
        innerRef.current.style.transform = `translateY(${-offsetRef.current}px)`;
      }
      animRef.current = requestAnimationFrame(animate);
    };
    animRef.current = requestAnimationFrame(animate);
    return () => { if (animRef.current) cancelAnimationFrame(animRef.current); };
  }, [overflows]);

  return (
    <div
      className={`ops-commit-scroll ${overflows ? "ops-commit-scroll-masked" : ""}`}
      ref={containerRef}
    >
      <div ref={innerRef} className="ops-commit-inner">
        {items.length === 0 ? (
          <div className="ops-card-empty">{emptyMsg}</div>
        ) : (
          displayItems.map((item, i) => (
            <div key={i} className={rowClass}>{renderRow(item)}</div>
          ))
        )}
      </div>
    </div>
  );
};

const CommitColumn = ({ items, rightItems, wsNum, currentCycles }) => {
  const cycleName = () => {
    const cycle = (currentCycles || []).find(
      (c) => String(c.workstream_num) === String(wsNum)
    );
    return cycle ? cycle.name : `WS ${wsNum}`;
  };

  const expectedPct = 100;

  const renderLeft = (row) => {
    const pctStr = row.commitment_percent;
    const pct = pctStr != null ? parseFloat(pctStr) : null;
    let color;
    if (pct === null || isNaN(pct)) {
      color = undefined;
    } else if (expectedPct === 0) {
      color = interpolateColor(1);
    } else {
      color = interpolateColor(Math.min(pct / expectedPct, 1));
    }
    return (
      <>
        <span className="ops-commit-name">{row.program_name}</span>
        <span className="ops-commit-pct" style={{ color }}>
          {pctStr || "—"}
        </span>
      </>
    );
  };

  const renderRight = (row) => {
    const label = row?.swimlane || row?.label || row?.program_name || "—";
    const pctStr = row?.commitment_percent ?? row?.percent;
    const pct = pctStr != null ? parseFloat(pctStr) : null;
    let color;
    if (pct === null || isNaN(pct)) {
      color = undefined;
    } else {
      color = interpolateColor(Math.min(pct / 100, 1));
    }
    return (
      <>
        <span className="ops-commit-right-label">{label}</span>
        <span className="ops-commit-right-pct" style={{ color }}>
          {pctStr || "—"}
        </span>
      </>
    );
  };

  return (
    <div className="ops-commit-col">
      <h3 className="ops-commit-col-title">{cycleName()}</h3>
      <div className="ops-commit-th-row">
        <div className="ops-commit-th-half">
          <span className="ops-commit-th-label">Client</span>
          <span className="ops-commit-th-label ops-commit-th-label-right">Achieved</span>
        </div>
        <span className="ops-commit-th-divider" />
        <div className="ops-commit-th-half">
          <span className="ops-commit-th-label">Role Type</span>
          <span className="ops-commit-th-label ops-commit-th-label-right">Achieved</span>
        </div>
      </div>
      <div className="ops-commit-lanes">
        <ScrollingLane
          items={items || []}
          renderRow={renderLeft}
          emptyMsg="No commitments"
          rowClass="ops-commit-left-row"
        />
        <div className="ops-commit-lane-divider" />
        <ScrollingLane
          items={rightItems || []}
          renderRow={renderRight}
          emptyMsg="—"
          rowClass="ops-commit-right-row"
        />
      </div>
    </div>
  );
};

// eslint-disable-next-line no-unused-vars
const ClientCommitments = ({ data, rightData, currentCycles }) => {
  const isActive = (d) => d.commitment_percent != null && parseFloat(d.commitment_percent) > 0;
  const ws1 = (data || []).filter((d) => String(d.workstream_num) === "1" && isActive(d));
  const ws2 = (data || []).filter((d) => String(d.workstream_num) === "2" && isActive(d));
  const sortBySwimlaneAsc = (a, b) =>
    String(a?.swimlane || "").localeCompare(String(b?.swimlane || ""));
  const ws1Right = (rightData || [])
    .filter((d) => String(d.workstream_num) === "1")
    .sort(sortBySwimlaneAsc);
  const ws2Right = (rightData || [])
    .filter((d) => String(d.workstream_num) === "2")
    .sort(sortBySwimlaneAsc);

  return (
    <div
      className="ops-card ops-commitments-card"
      style={{ borderTop: "3px solid #6366f1" }}
    >
      <div className="ops-card-header">
        <span className="ops-card-icon">🎯</span>
        <h3 className="ops-card-title">
          Client Commitments <span className="ops-card-title-sub">(Previous Cycle)</span>
        </h3>
      </div>
      <div className="ops-commit-columns">
        <CommitColumn items={ws1} rightItems={ws1Right} wsNum={1} currentCycles={currentCycles} />
        <div className="ops-commit-divider" />
        <CommitColumn items={ws2} rightItems={ws2Right} wsNum={2} currentCycles={currentCycles} />
      </div>
    </div>
  );
};

// ── Horizontal Carousel ──
const CAROUSEL_SPEED = 0.3; // px per frame — very slow

// eslint-disable-next-line no-unused-vars
const HorizontalCarousel = ({ children }) => {
  const trackRef = useRef(null);
  const offsetRef = useRef(0);
  const animRef = useRef(null);
  const [totalWidth, setTotalWidth] = useState(0);
  const panelOffsetsRef = useRef([]);
  const [paused, setPaused] = useState(false);

  // Measure the single set width and record each panel's left offset.
  useEffect(() => {
    if (!trackRef.current) return;
    const panels = trackRef.current.children;
    const half = Math.floor(panels.length / 2);
    const offsets = [];
    let w = 0;
    for (let i = 0; i < half; i++) {
      offsets.push(w);
      w += panels[i].offsetWidth;
    }
    panelOffsetsRef.current = offsets;
    setTotalWidth(w);
  }, [children]);

  // Reset scroll position only when content width changes.
  useEffect(() => {
    offsetRef.current = 0;
  }, [totalWidth]);

  // Auto-scroll animation; pauses when `paused` is true.
  useEffect(() => {
    if (totalWidth <= 0 || !trackRef.current || paused) return;

    const animate = () => {
      offsetRef.current += CAROUSEL_SPEED;
      if (offsetRef.current >= totalWidth) offsetRef.current -= totalWidth;
      if (trackRef.current) {
        trackRef.current.style.transform = `translateX(${-offsetRef.current}px)`;
      }
      animRef.current = requestAnimationFrame(animate);
    };
    animRef.current = requestAnimationFrame(animate);
    return () => {
      if (animRef.current) cancelAnimationFrame(animRef.current);
    };
  }, [totalWidth, paused]);

  // Dev/testing: ←/→ jumps to prev/next panel and pauses. Space toggles auto-scroll.
  useEffect(() => {
    const handleKey = (e) => {
      const tag = e.target?.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA" || e.target?.isContentEditable) return;

      const offsets = panelOffsetsRef.current;
      if (totalWidth <= 0 || offsets.length === 0) return;

      if (e.key === "ArrowRight" || e.key === "ArrowLeft") {
        e.preventDefault();
        setPaused(true);

        const current = ((offsetRef.current % totalWidth) + totalWidth) % totalWidth;
        let target;
        if (e.key === "ArrowRight") {
          target = offsets.find((o) => o > current + 1);
          if (target == null) target = offsets[0];
        } else {
          target = null;
          for (let i = offsets.length - 1; i >= 0; i--) {
            if (offsets[i] < current - 1) { target = offsets[i]; break; }
          }
          if (target == null) target = offsets[offsets.length - 1];
        }
        offsetRef.current = target;
        if (trackRef.current) {
          trackRef.current.style.transform = `translateX(${-offsetRef.current}px)`;
        }
      } else if (e.key === " " || e.code === "Space") {
        e.preventDefault();
        setPaused((p) => !p);
      }
    };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [totalWidth]);

  // Duplicate children for seamless wrap
  return (
    <div className="ops-carousel-viewport">
      <div className="ops-carousel-track" ref={trackRef}>
        {children}
        {children}
      </div>
    </div>
  );
};

const OpsDashboard = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      const response = await fetch(
        `${config.api.baseUrl}${config.api.endpoints.opsDashboard.data}`,
        { method: "GET", credentials: "include" }
      );
      if (!response.ok) throw new Error("Failed to fetch dashboard data");
      const json = await response.json();
      if (json.success && json.data) {
        setData(json.data);
        setError(null);
      }
    } catch (err) {
      console.error("Error fetching ops dashboard data:", err);
      if (!data) setError("Failed to load dashboard data.");
    } finally {
      setLoading(false);
    }
  }, [data]);

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, POLL_INTERVAL);
    return () => clearInterval(interval);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  if (loading && !data) {
    return (
      <div className="ops-dashboard">
        <div className="ops-loading-full">Loading dashboard...</div>
      </div>
    );
  }

  if (error && !data) {
    return (
      <div className="ops-dashboard">
        <div className="ops-loading-full">{error}</div>
      </div>
    );
  }

  return (
    <div className="ops-dashboard">
      {/* Top bar: Workstream Revenue by Status — full width, no scrolling */}
      <div className="ops-top-bar">
        <WorkstreamTable
          data={data?.workstream_revenue_pct_by_status || []}
          currentCycles={data?.current_cycles || []}
        />
      </div>

      {/* Main split: Revenue by Role on the left, empty slot on the right */}
      <div className="ops-main-split">
        <div className="ops-main-left">
          <div className="ops-placeholder-wrapper">
            <h2 className="ops-section-title">Hours by Role</h2>
            <RoleRevenueTable
              data={data?.role_revenue_pct_by_status || []}
              currentCycles={data?.current_cycles || []}
              showHours
            />
          </div>
        </div>
        <div className="ops-main-right">
          <div className="ops-placeholder-wrapper">
            <h2 className="ops-section-title">Hours by Client</h2>
            <RoleRevenueTable
              data={data?.program_revenue_pct_by_status || []}
              currentCycles={data?.current_cycles || []}
              nameLabel="Client"
              fitNameColumn
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default OpsDashboard;
